import BrandMark from "../components/brand/BrandMark";
import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

const API = '/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [userEmail, setUserEmail] = useState("");

  // Validate token on page load WITHOUT invalidating it
  useEffect(() => {
    // RC-WEB-3 §3 — the token lives in the query string: never leak it via Referer.
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError("Este enlace ya no es válido o ha vencido.");
        setValidating(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/validate-reset-token/${token}`);
        if (response.data.valid) {
          setUserEmail(response.data.email || "");
          setTokenError(null);
        } else {
          setTokenError(response.data.error || "Este enlace ya no es válido o ha vencido.");
        }
      } catch (error) {
        setTokenError("Este enlace ya no es válido o ha vencido.");
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("Contraseña actualizada");
      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al restablecer contraseña";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="border-slate-100 shadow-lg max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-slate-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Validando enlace...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Token error state
  if (tokenError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            <BrandMark size="md" asLink={false} />
          </Link>

          <Card className="border-slate-100 shadow-lg" data-testid="reset-invalid-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Enlace no válido
              </h2>
              <p className="text-slate-600 mb-6" data-testid="reset-invalid-message">
                Este enlace ya no es válido o ha vencido.
              </p>
              <Link to="/forgot-password">
                <Button className="w-full bg-slate-900 hover:bg-slate-800" data-testid="request-new-link-btn">
                  Solicitar un nuevo enlace
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            <BrandMark size="md" asLink={false} />
          </Link>

          <Card className="border-slate-100 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                ¡Contraseña Actualizada!
              </h2>
              <p className="text-slate-600 mb-6">
                Tu contraseña ha sido cambiada exitosamente. Serás redirigido al login...
              </p>
              <Link to="/login">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="go-to-login-btn">
                  Ir a Iniciar Sesión
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <BrandMark size="md" asLink={false} />
        </Link>

        <Card className="border-slate-100 shadow-lg">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nueva Contraseña
            </CardTitle>
            <CardDescription className="text-slate-600">
              Ingresa tu nueva contraseña
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Nueva contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-12 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 pr-12"
                    autoComplete="new-password"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    aria-pressed={showPassword}
                    data-testid="reset-toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">Confirmar contraseña</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    className="h-12 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 pr-12"
                    data-testid="reset-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600"
                    aria-label={showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"}
                    aria-pressed={showConfirm}
                    data-testid="reset-toggle-confirm-password"
                  >
                    {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  "Guardando..."
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Cambiar Contraseña
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
